import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexTheme,
  ApexTooltip,
  ApexXAxis,
  ChartComponent,
} from 'ng-apexcharts';
import { ApiService } from 'src/app/services/api/api.service';

export type ChartOptions = {
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels: any;
  colors: string[];
  dataLabels: ApexDataLabels;
  fill: ApexFill;
  plotOptions: ApexPlotOptions;
  xaxis: ApexXAxis;
  tooltip: ApexTooltip;
  theme: ApexTheme;
};

@Component({
  selector: 'app-dashboard-dedicated',
  templateUrl: './dashboard-dedicated.component.html',
  styleUrls: ['./dashboard-dedicated.component.scss'],
})
export class DashboardDedicatedComponent implements OnInit, OnDestroy {
  @ViewChild('conversionsChart1') conversionsChart1!: ChartComponent;
  @ViewChild('conversionsChart2') conversionsChart2!: ChartComponent;
  conversion1!: Partial<ChartOptions>;
  conversion2!: Partial<ChartOptions>;
  conversion1Color = ['#73b4ff'];
  conversion2Color = ['#59e0c5'];
  dataSeries1: number[] = [];
  dataSeries2: number[] = [];
  intervalSub: any;
  services: any[] = [];
  service: any;
  nodeId!: number;
  interface: string = '';
  traffic: any[] = [];
  upload: string = '';
  download: string = '';
  status: string = '';
  isLoading: boolean = false;

  constructor(private _api: ApiService) {
    this.dataSeries1 = this.getInitialSeries();
    this.dataSeries2 = this.getInitialSeries();

    this.conversion1 = {
      chart: {
        type: 'bar',
        height: 65,
        sparkline: {
          enabled: true,
        },
        background: 'transparent',
      },
      dataLabels: {
        enabled: false,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0,
          gradientToColors: ['#4099ff'],
          inverseColors: true,
          opacityFrom: 0.99,
          opacityTo: 0.99,
          stops: [0, 100],
        },
      },
      plotOptions: {
        bar: {
          columnWidth: '80%',
        },
      },
      series: [
        {
          data: this.dataSeries1,
        },
      ],
      xaxis: {
        crosshairs: {
          width: 1,
        },
      },
      tooltip: {
        fixed: {
          enabled: false,
        },
        x: {
          show: false,
        },
        marker: {
          show: false,
        },
      },
      theme: {
        mode: 'light',
      },
    };

    this.conversion2 = {
      chart: {
        type: 'bar',
        height: 65,
        sparkline: {
          enabled: true,
        },
        background: 'transparent',
      },
      dataLabels: {
        enabled: false,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0,
          gradientToColors: ['#59e0c5'],
          inverseColors: true,
          opacityFrom: 0.99,
          opacityTo: 0.99,
          stops: [0, 100],
        },
      },
      plotOptions: {
        bar: {
          columnWidth: '80%',
        },
      },
      series: [
        {
          data: this.dataSeries2,
        },
      ],
      xaxis: {
        crosshairs: {
          width: 1,
        },
      },
      tooltip: {
        fixed: {
          enabled: false,
        },
        x: {
          show: false,
        },
        marker: {
          show: false,
        },
      },
      theme: {
        mode: 'light',
      },
    };
  }

  ngOnInit(): void {
    this.getDedicatedServices();
    this.intervalSub = setInterval(() => {
      if (this.nodeId && this.interface) {
        this.getTraffic(this.nodeId, this.interface);
      }
    }, 3000);
  }

  getDedicatedServices() {
    this.isLoading = true;

    this._api.customer.getDedicatedServices().subscribe({
      next: (resp) => {
        this.services = resp;
        this.service = resp.length > 0 ? resp[0] : null;

        this.interface = this.service.dedicatedService?.monitoringInterface;
        this.nodeId = this.service.dedicatedService?.nodeId;

        this.getTraffic(this.nodeId, this.interface);
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  getTraffic(nodeId: number, routerInterface: string) {
    this._api.router.getTrafficInRealTimeByInterface(nodeId, routerInterface).subscribe({
      next: (resp) => {
        this.traffic = resp;

        this.getNewSeries(Number(this.traffic[0].data), Number(this.traffic[1].data));
        this.formatTraffic();
        this.status = 'ACTIVO';
        this.isLoading = false;
      },
      error: () => {
        this.status = 'INACTIVO';
        this.isLoading = false;
      },
    });
  }

  getInitialSeries() {
    const series: number[] = [];
    for (let i = 0; i < 20; i++) {
      series.push(0);
    }

    return series;
  }

  formatTraffic() {
    this.upload = this.convertBytes(Number(this.traffic[0].data));
    this.download = this.convertBytes(Number(this.traffic[1].data));
  }

  convertBytes(value: number) {
    if (value > 1024) {
      return (value / 1024).toFixed(2) + ' Mbps';
    } else {
      return value.toFixed(2) + ' Kbps';
    }
  }

  addItem(series: number[], item: number) {
    series.push(item);
    series.shift();
    return series;
  }

  getNewSeries(serie1: number, serie2: number) {
    this.dataSeries1 = this.addItem(this.dataSeries1, serie1);
    this.dataSeries2 = this.addItem(this.dataSeries2, serie2);

    if(this.conversionsChart1 && this.conversionsChart1.chart) {
      this.conversionsChart1.updateSeries([{ data: this.dataSeries1 }], true);
    }
    if(this.conversionsChart2 && this.conversionsChart2.chart) {
      this.conversionsChart2.updateSeries([{ data: this.dataSeries2 }], true);
    }
  }

  ngOnDestroy() {
    if (this.intervalSub) {
      clearInterval(this.intervalSub);
    }
  }
}
